<template>
  <div class="modal-overlay" v-if="isOpen" @click.self="close">
    <div class="modal-content">
      <h2>{{ editTile && !editTile.isEmpty ? 'ブックマーク編集' : 'ブックマーク追加' }}</h2>
      
      <div class="form-group">
        <label for="bookmark-url">URL <span class="required">*</span></label>
        <input 
          type="url" 
          id="bookmark-url" 
          ref="urlInput"
          v-model="bookmark.url" 
          placeholder="https://example.com"
          class="form-control"
        >
      </div>
      
      <div class="form-group">
        <label for="bookmark-name">名称</label>
        <input 
          type="text" 
          id="bookmark-name" 
          v-model="bookmark.name" 
          placeholder="サイト名（空欄の場合は自動取得します）"
          class="form-control"
        >
      </div>
      
      <div class="form-group">
        <label for="bookmark-color">背景色</label>
        <!-- 6色のプリセットカラーチップを追加 -->
        <div class="color-chips">
          <div 
            v-for="(color, name) in colorPresets" 
            :key="name"
            class="color-chip" 
            :style="{ backgroundColor: color }"
            :title="name"
            @click="selectColor(color)"
          ></div>
        </div>
        <input 
          type="color" 
          id="bookmark-color" 
          v-model="bookmark.color"
          class="form-control color-picker"
        >
      </div>
      
      <div class="modal-buttons">
        <button class="btn btn-cancel" @click="close">キャンセル</button>
        <button 
          class="btn btn-save" 
          :class="{ 'btn-active': isFormValid }"
          :disabled="!isFormValid" 
          @click="save"
        >保存</button>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchSiteTitle, getFaviconUrl } from '@/services/siteInfo';

export default {
  props: {
    isOpen: Boolean,
    editTile: Object
  },
  
  data() {
    return {
      bookmark: {
        name: '',
        url: '',
        iconUrl: '',
        color: '#f0f0f0'
      },
      isLoading: false
    };
  },
  
  computed: {
    // フォームのバリデーション - URLが入力されているか確認
    isFormValid() {
      return this.bookmark.url && this.bookmark.url.trim() !== '';
    },
    colorPresets() {
      return {
        'ブルー': '#A7C6E2',
        'グリーン': '#B7D9BB',
        'ピンク': '#F3B6B1',
        'イエロー': '#F3D298',
        'グレー': '#D2D2D2',
        'オレンジ': '#E8A89E'
      };
    }
  },
  
  watch: {
    isOpen(newVal) {
      if (newVal && this.editTile) {
        // モーダルが開かれたときにデータをセット
        this.bookmark = {
          name: this.editTile.isEmpty ? '' : this.editTile.name || '',
          url: this.editTile.url || '',
          iconUrl: this.editTile.isEmpty ? '' : this.editTile.iconUrl || '',
          color: this.editTile.color || '#f0f0f0'
        };
        
        // モーダルが開いた後にURLフィールドにフォーカスを当てる
        this.$nextTick(() => {
          if (this.$refs.urlInput) {
            this.$refs.urlInput.focus();
          }
        });
      }
    }
  },
  
  methods: {
    close() {
      this.$emit('close');
    },
    
    async save() {
      // 入力値のバリデーション（URLのみ必須）
      if (!this.bookmark.url) {
        alert('URLを入力してください');
        return;
      }
      
      // URLの形式チェック（http:// または https:// で始まっていない場合は追加）
      let url = this.bookmark.url.trim();
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }
      
      // 名前が空欄の場合、APIを使用してサイト名を取得
      let name = this.bookmark.name;
      if (!name) {
        try {
          this.isSaving = true;
          name = await fetchSiteTitle(url);
          
          // APIからタイトルが取得できなかった場合はドメインを使用
          if (!name) {
            const domain = new URL(url).hostname;
            name = domain.replace(/^www\./, '');
          }
        } catch (error) {
          console.error('サイトタイトル取得エラー:', error);
          // エラー発生時はドメインを使用
          try {
            const domain = new URL(url).hostname;
            name = domain.replace(/^www\./, '');
          } catch (e) {
            name = 'ブックマーク';
          }
        } finally {
          this.isSaving = false;
        }
      }
      
      // ファビコンURLの設定（Google Favicon APIを使用）
      let iconUrl;
      try {
        iconUrl = getFaviconUrl(url);
      } catch (e) {
        // エラーが発生した場合はデフォルトアイコンを使用
        iconUrl = '/bookmark.svg?color=AAA';
      }
      
      // 親コンポーネントに保存イベントを発行
      this.$emit('save', {
        name,
        url,
        iconUrl,
        color: this.bookmark.color
      });
    },
    selectColor(color) {
      this.bookmark.color = color;
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start; /* centerからflex-startに変更して上部に配置 */
  padding-top: 10vh; /* 上部から少し空間を開ける */
  z-index: 1000;
  overflow-y: auto; /* スクロール可能に */
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.required {
  color: #ff4d4f;
}

input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn {
  padding: 8px 16px;
  margin-left: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-cancel {
  background-color: #f1f1f1;
  color: #333;
}

.btn-save {
  background-color: #cccccc; /* 非アクティブ時の色 */
  color: white;
  min-width: 80px;
  transition: background-color 0.3s ease;
}

.btn-active {
  background-color: #e9d0ca; /* アクティブ時の色 */
}

.btn-save:disabled {
  cursor: not-allowed;
}

.color-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.color-chip {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: transform 0.2s, border-color 0.2s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.color-chip:hover {
  transform: scale(1.1);
}

.color-chip.active {
  border-color: #333;
}

.color-picker {
  height: 36px;
  padding: 0;
  margin-top: 5px;
}

/* モバイル向けのスタイル調整 */
@media (max-width: 600px) {
  .modal-overlay {
    align-items: flex-start;
    padding-top: 5vh; /* モバイルではさらに上部に */
  }
  
  .modal-content {
    width: 95%; /* より幅を広く */
    max-height: 85vh; /* 高さ制限を設定 */
    overflow-y: auto; /* コンテンツが多い場合はスクロール */
    padding: 15px; /* パディングを少し減らす */
  }
  
  input[type="url"], 
  input[type="text"] {
    font-size: 16px; /* iOSでの自動ズームを防止 */
  }
  
  .form-group {
    margin-bottom: 12px; /* 間隔を少し縮める */
  }
  
  /* 色選択チップを小さくして表示を確保 */
  .color-chip {
    width: 28px;
    height: 28px;
  }
}
</style>